import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setUnauthenticatedHandler, setWorkspaceId } from './api';
import { startRealtime } from './realtime';
import { applyBrand } from './theme';

export type Membership = {
  id: string; name: string; slug: string; status: string; role: string; availability: string;
  permissions: string[]; branding: Record<string, any>; locale: string; timezone: string; onboarding: Record<string, any>;
};
export type Me = {
  user: { id: string; email: string; name: string; avatar_url?: string; locale: string; theme: string;
    email_verified_at: string | null; platform_role: string; mfa_enabled: boolean };
  workspaces: Membership[];
  impersonating?: boolean;
};

type AuthCtx = {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<Me | null>;
  setMe: (m: Me | null) => void;
  logout: () => Promise<void>;
  mfaPending: boolean;
  setMfaPending: (v: boolean) => void;
};
const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const m = await api<Me>('/api/v1/auth/me', { workspace: null });
      setMe(m); setMfaPending(false);
      return m;
    } catch (e: any) {
      setMe(null);
      if (e?.code === 'mfa_required') setMfaPending(true);
      return null;
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    setUnauthenticatedHandler((code) => {
      if (code === 'mfa_required') setMfaPending(true);
      setMe(null);
    });
  }, []);

  const logout = useCallback(async () => {
    try { await api('/api/v1/auth/logout', { method: 'POST', body: {}, workspace: null }); } catch { /* already gone */ }
    setMe(null); setWorkspaceId(null); startRealtime(null); applyBrand(null);
  }, []);

  const value = useMemo(() => ({ me, loading, refresh, setMe, logout, mfaPending, setMfaPending }), [me, loading, refresh, logout, mfaPending]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth outside provider');
  return c;
}

/* ───── Current workspace (from the :slug route param) ───── */
type WsCtx = { ws: Membership; can: (perm: string) => boolean; base: string; path: (p: string) => string };
const WorkspaceCtx = createContext<WsCtx | null>(null);

export function WorkspaceProvider({ ws, children }: { ws: Membership; children: ReactNode }) {
  setWorkspaceId(ws.id);
  useEffect(() => {
    setWorkspaceId(ws.id);
    startRealtime(ws.id);
    applyBrand(ws.branding?.primary_color);
    try { localStorage.setItem('bf_last_ws', ws.slug); } catch { /* ignore */ }
  }, [ws.id, ws.slug, ws.branding?.primary_color]);
  const value = useMemo(() => {
    const perms = new Set(ws.permissions);
    const base = `/w/${ws.slug}`;
    return { ws, can: (p: string) => perms.has(p), base, path: (p: string) => `${base}${p.startsWith('/') ? p : `/${p}`}` };
  }, [ws]);
  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export function useWorkspace() {
  const c = useContext(WorkspaceCtx);
  if (!c) throw new Error('useWorkspace outside WorkspaceProvider');
  return c;
}
